// Canonical query keys for every server resource.
//
// Pages should never spell out a raw array key — always use the helpers
// here. This is the single source of truth for cache invalidation:
// changing a mutation? Invalidate the matching helper and every page using
// it picks up the fresh data.
//
// Convention: keys are tuples whose first element identifies the resource
// (e.g. "monitors") and whose tail describes the query (id, filters, etc.).
// Calling `keys.monitors.all` gives the prefix that invalidates every
// monitors query.

export const keys = {
  monitors: {
    all: ["monitors"],
    list: (filters) => ["monitors", "list", filters ?? {}],
    detail: (id) => ["monitors", "detail", id],
    checks: (id, params) => ["monitors", id, "checks", params ?? {}],
    responseTimes: (id, params) => ["monitors", id, "response-times", params ?? {}],
    uptime: (id, params) => ["monitors", id, "uptime", params ?? {}],
    alertRules: (id) => ["monitors", id, "alert-rules"],
    locations: (id) => ["monitors", id, "locations"],
  },
  incidents: {
    all: ["incidents"],
    list: (filters) => ["incidents", "list", filters ?? {}],
    detail: (id) => ["incidents", "detail", id],
    timeline: (id) => ["incidents", id, "timeline"],
  },
  groups: {
    all: ["groups"],
    list: ["groups", "list"],
    detail: (id) => ["groups", "detail", id],
  },
  sites: {
    all: ["sites"],
    list: ["sites", "list"],
    detail: (id) => ["sites", "detail", id],
    dashboard: (id) => ["sites", "dashboard", id],
  },
  channels: {
    all: ["channels"],
    list: ["channels", "list"],
    detail: (id) => ["channels", "detail", id],
  },
  statusPages: {
    all: ["status-pages"],
    list: ["status-pages", "list"],
    detail: (id) => ["status-pages", "detail", id],
    subscribers: (id) => ["status-pages", id, "subscribers"],
    messages: (id) => ["status-pages", id, "messages"],
  },
  maintenance: {
    all: ["maintenance"],
    list: ["maintenance", "list"],
  },
  members: {
    all: ["members"],
    list: ["members", "list"],
  },
  workspaces: {
    all: ["workspaces"],
    list: ["workspaces", "list"],
    detail: (id) => ["workspaces", "detail", id],
    members: (id) => ["workspaces", id, "members"],
  },
  oncall: {
    all: ["oncall"],
    schedules: ["oncall", "schedules"],
    schedule: (id) => ["oncall", "schedule", id],
    escalationPolicies: ["oncall", "escalation-policies"],
  },
  inboundWebhooks: {
    all: ["inbound-webhooks"],
    list: ["inbound-webhooks", "list"],
    detail: (id) => ["inbound-webhooks", "detail", id],
    events: (id) => ["inbound-webhooks", "events", id],
  },
  teams: {
    all: ["teams"],
    list: ["teams", "list"],
    detail: (id) => ["teams", "detail", id],
  },
  services: {
    all: ["services"],
    list: ["services", "list"],
  },
  routingRules: {
    all: ["routing-rules"],
    list: ["routing-rules", "list"],
  },
  auditLog: {
    all: ["audit-log"],
    list: (filters) => ["audit-log", "list", filters ?? {}],
  },
  apiKeys: { all: ["api-keys"], list: ["api-keys", "list"] },
  alerts: {
    all: ["alerts"],
    rules: ["alerts", "rules"],
    channels: ["alerts", "channels"],
  },
  billing: { all: ["billing"], current: ["billing", "current"] },
  permissions: { current: ["permissions", "current"] },
  me: { current: ["me"] },
  ai: { conversations: ["ai", "conversations"], messages: (id) => ["ai", "messages", id] },
  dashboard: { summary: ["dashboard", "summary"] },
  locations: { all: ["locations"], list: ["locations", "list"] },
  slack: { connection: ["slack", "connection"] },
};
